import React, { useState } from 'react';
import {
  Space,
  Typography,
  Tabs,
  Steps,
  ConfigProvider,
  Divider,
  message,
  Row,
  Col,
  Skeleton,
} from 'antd';
import enUS from 'antd/es/locale/en_US';
import WorkReportTable from './WorkReport/WorkReportTable';
import { useTranslation } from 'react-i18next';
import {
  GetInternshipApplicationsById,
  GetInternshipById,
} from '../helper/apiHelper';
import type {
  CombinedInternshipApplicationWithProcesses,
  ProcessSteps,
} from '../Types/interfaces';
import { StepName } from '../Types/interfaces';
import { Roles } from '../Types/interfaces';
import { EvaluationReportDisplay } from './EvaluationForm/Display/EvaluationReportDisplayComponent';
import styles from '../Assets/Styles/InternshipProcess.module.css';
import FileUploader from './FileUploader';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { EMPTY_ARRAY } from '../Constants/constants';
import { useStudent } from '../hooks/useStudent';
import { useCurrentUser } from '../hooks/useCurrentUser';

const { Text } = Typography;

const renderStatusForApplication = (
  status: number
): 'wait' | 'process' | 'finish' | 'error' => {
  switch (status) {
    case 0:
      return 'process';
    case 1:
      return 'finish';
    case 2:
      return 'error';
    default:
      return 'wait';
  }
};

const renderStatus = (
  processes: [ProcessSteps] | null,
  stepName: StepName
): 'wait' | 'process' | 'finish' | 'error' => {
  if (processes === null) {
    return 'wait';
  }

  const step = processes.find((e) => e.stepName === stepName);

  if (!step) {
    return 'wait';
  }

  if (step.disabled) {
    return 'wait';
  }

  switch (step.stepStatus) {
    case 0:
      return 'wait';
    case 1:
      return 'process';
    case 2:
      return 'process';
    case 3:
      return 'finish';
    default:
      return 'wait';
  }
};

const isEnabledStep = (
  processes: [ProcessSteps] | null,
  stepName: string
): boolean => {
  if (!processes) return false;
  const step = processes.find((e) => e.stepName === stepName);
  if (!step) return false;
  return step.disabled;
};

const enableSignature = (processes: [ProcessSteps] | null): boolean => {
  if (processes === null) {
    return false;
  }
  const step = processes.find(
    (e) => e.stepName === 'ConventionFillingAndSigningByStudent'
  );

  if (step && !step.disabled) {
    return step.stepStatus === 1 || step.stepStatus === 2;
  }

  return false;
};

const isSigned = (step: ProcessSteps | undefined): boolean => {
  if (step === undefined) {
    return false;
  }

  return step?.stepStatus === 2;
};

const enableStudentWorkReportSignature = (
  processes: [ProcessSteps] | null
): boolean => {
  if (processes === null) {
    return false;
  }
  const step = processes.find(
    (e) => e.stepName === 'SigningWorkReportByStudent'
  );
  return step ? step.stepStatus === 1 || step.stepStatus === 2 : false;
};

const InternshipProcessComponent = (): React.ReactElement => {
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [file, setFile] = useState<File | null>(null);
  const queryClient = useQueryClient();
  const { data: student } = useStudent(loggedInUser?.id);

  const applicationIdArr = student?.internshipApplications;
  const processIdArr = student?.internshipProcesses;

  const {
    data: internshipApplications = EMPTY_ARRAY,
    isLoading,
    refetch: reFetchData,
  } = useQuery({
    queryKey: [
      ...QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
      applicationIdArr,
      processIdArr,
    ],
    queryFn: async () => {
      if (
        applicationIdArr &&
        processIdArr &&
        applicationIdArr.length === 0 &&
        processIdArr.length === 0
      ) {
        return [];
      }

      const internshipProcessesResponse = await Promise.all(
        processIdArr?.filter((e) => e !== 0).map(GetInternshipById) ?? []
      );
      if (!applicationIdArr) return [];
      const internshipApplicationResponse = await Promise.all(
        applicationIdArr
          .filter((e) => e !== 0)
          .map(async (e) => {
            const res = await GetInternshipApplicationsById(e);

            const application = res;
            let process = null;
            let processSteps = null;

            if (application.status === 1) {
              process = internshipProcessesResponse.find(
                (p) => p.internshipApplicationId === application.id
              );

              if (process) {
                processSteps = process.processSteps;
              }
            }
            return {
              application: application,
              process: process,
              processSteps: processSteps,
            };
          })
      );
      return internshipApplicationResponse;
    },
    enabled:
      applicationIdArr &&
      processIdArr &&
      applicationIdArr?.length > 0 &&
      processIdArr?.length > 0,
    meta: {
      onError: (error: unknown) => {
        messageApi.error(String(error));
      },
    },
  });

  const SkeletonCards = (): React.ReactElement => {
    return (
      <>
        <Row justify="space-around" style={{ marginTop: '100px' }}>
          <Col span={8}>
            <Skeleton active paragraph={{ rows: 15 }}></Skeleton>
          </Col>
          <Col span={8}>
            <Skeleton active paragraph={{ rows: 15 }}></Skeleton>
          </Col>
        </Row>
        <Row style={{ marginTop: '50px' }}>
          <Skeleton active />
        </Row>
      </>
    );
  };

  const assignedMentorToStudent = (
    item: CombinedInternshipApplicationWithProcesses
  ): boolean => {
    return (
      item.processSteps?.find(
        (e: ProcessSteps) => e.stepName === 'AllocatingTutorFromOrganization'
      )?.stepStatus === 3
    );
  };

  const updateSteps = async (processId: number | undefined): Promise<void> => {
    if (!processId) {
      return;
    }
    await reFetchData();
  };

  const subTitle = t('internshipProcessComp.unsupportedProcess');

  if (applicationIdArr && applicationIdArr.length > 0) {
    if (isLoading) {
      return SkeletonCards();
    }
    return (
      <Space direction="vertical" style={{ display: 'block', paddingTop: 10 }}>
        {contextHolder}
        <Tabs
          tabPosition="top"
          centered
          tabBarGutter={16}
          size="large"
          type="card"
          items={internshipApplications.map(
            (item: CombinedInternshipApplicationWithProcesses) => {
              return {
                key: String(item.application.internshipOfferId),
                label: `${String(item.application.internshipOfferName)} | ${String(item.application.organizationName)}`,
                children: (
                  <>
                    <Typography.Text strong>
                      {t('internshipProcessComp.organization')}:{' '}
                      {item.application.organizationName}
                    </Typography.Text>
                    <br />
                    <Typography.Text strong>
                      {t('internshipProcessComp.status')}:{' '}
                      {((): string => {
                        if (item.application.status === 0) {
                          return ' ' + t('internshipProcessComp.pending');
                        } else if (item.application.status === 1) {
                          return ' ' + t('internshipProcessComp.accepted');
                        } else if (item.application.status === 2) {
                          return ' ' + t('internshipProcessComp.pending');
                        } else {
                          return '';
                        }
                      })()}
                    </Typography.Text>

                    <Row gutter={8} justify={'space-around'}>
                      <Col
                        xs={24}
                        sm={24}
                        lg={12}
                        style={{ maxWidth: 500, marginBottom: 20 }}
                      >
                        <Divider style={{ fontSize: 18 }}>
                          {t('internshipProcessComp.orgProcesses')}
                        </Divider>
                        <Steps
                          direction="vertical"
                          items={[
                            {
                              title: t('internshipProcessComp.accepting'),
                              status: renderStatusForApplication(
                                item.application.status
                              ),
                            },
                            {
                              title: t('internshipProcessComp.assignMentor'),
                              status: renderStatus(
                                item.processSteps,
                                StepName.allocatingTutorFromOrganization
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.allocatingTutorFromOrganization
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.allocatingTutorFromOrganization
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t('internshipProcessComp.fillWorkReport'),
                              status: renderStatus(
                                item.processSteps,
                                StepName.workReportFilling
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.workReportFilling
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.workReportFilling
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.validateWorkReportByTutor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.validateWorkReportByTutor
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.validateWorkReportByTutor
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.validateWorkReportByTutor
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.signWorkReportByStudent'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.signingWorkReportByStudent
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.signingWorkReportByStudent
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.signingWorkReportByStudent
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.signWorkReportByTutor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.signingWorkReportByTutor
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.signingWorkReportByTutor
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.signingWorkReportByTutor
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.completeEvaluationByOrganization'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.completingEvaluationReportByOrganization
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.completingEvaluationReportByOrganization
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.completingEvaluationReportByOrganization
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.signEvaluationReportByTutor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.signingEvaluationReportByTutor
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.signingEvaluationReportByTutor
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.signingEvaluationReportByTutor
                              )
                                ? subTitle
                                : undefined,
                            },
                          ]}
                        />
                      </Col>
                      <Col
                        xs={24}
                        sm={24}
                        lg={12}
                        style={{ maxWidth: 500, marginBottom: 20 }}
                      >
                        {/* Content for the second column */}
                        <Divider style={{ fontSize: 18 }}>
                          {t('internshipProcessComp.universityProcesses')}
                        </Divider>
                        <Steps
                          direction="vertical"
                          items={[
                            {
                              title: t(
                                'internshipProcessComp.selectSupervisor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.allocatingUniversitySupervisorFromUBB
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.allocatingUniversitySupervisorFromUBB
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.allocatingUniversitySupervisorFromUBB
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.registeringConvention'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.registeringConventionDocument
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.registeringConventionDocument
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.registeringConventionDocument
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.fillConventionByStudent'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.conventionFillingAndSigningByStudent
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.conventionFillingAndSigningByStudent
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.conventionFillingAndSigningByStudent
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.signConventionByOrganization'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.signingInternshipConventionByOrganization
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByOrganization
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByOrganization
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.signConventionByTutor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.signingInternshipConventionByTutor
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByTutor
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByTutor
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.signConventionBySupervisor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.signingInternshipConventionByUniversitySupervisor
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByUniversitySupervisor
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByUniversitySupervisor
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.signConventionByRector'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.signingInternshipConventionByRector
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByRector
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.signingInternshipConventionByRector
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.gradeWorkReportBySupervisor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.gradingWorkReportByUniversitySupervisor
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.gradingWorkReportByUniversitySupervisor
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.gradingWorkReportByUniversitySupervisor
                              )
                                ? subTitle
                                : undefined,
                            },
                            {
                              title: t(
                                'internshipProcessComp.finalizeGradeBySupervisor'
                              ),
                              status: renderStatus(
                                item.processSteps,
                                StepName.finalizingGradeByUniversitySupervisor
                              ),
                              className: isEnabledStep(
                                item.processSteps,
                                StepName.finalizingGradeByUniversitySupervisor
                              )
                                ? styles.blurredStep
                                : undefined,
                              description: isEnabledStep(
                                item.processSteps,
                                StepName.finalizingGradeByUniversitySupervisor
                              )
                                ? subTitle
                                : undefined,
                            },
                          ]}
                        />

                        {enableSignature(item.processSteps) && item.process && (
                          <Row
                            justify="center"
                            align="stretch"
                            style={{ paddingTop: 10 }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'left',
                                alignItems: 'left',
                              }}
                            >
                              {enableSignature(item.processSteps) &&
                                !!item.process && (
                                  <FileUploader
                                    apiUrl={`${loggedInUser.id}/upload-internship-process-to-sign`}
                                    documentUrl={
                                      item.process.conventionDocumentUrl
                                    }
                                    documentSigningStatus={
                                      item.process.currentUniversityStepName
                                    }
                                    isSignatureDisabled={false}
                                    onFileUpload={async () => {
                                      queryClient.invalidateQueries({
                                        queryKey:
                                          QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
                                      });
                                    }}
                                    alreadySigned={isSigned(
                                      item.processSteps?.find(
                                        (e) =>
                                          e.stepName ===
                                          'ConventionFillingAndSigningByStudent'
                                      )
                                    )}
                                  />
                                )}
                            </div>
                          </Row>
                        )}
                      </Col>
                    </Row>
                    {!!item.process?.workLogId &&
                      assignedMentorToStudent(item) && (
                        <>
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('internshipProcessComp.workReport')}
                          </Divider>

                          <ConfigProvider locale={enUS}>
                            <WorkReportTable
                              StudentId={item.process.studentId}
                              ProcessId={item.process.id}
                              WorkLogId={item.process.workLogId}
                              WorkLogDocumentUrl={
                                item.process.workLogDocumentUrl
                              }
                              WorkLogDocumentStatus={
                                item.process.currentOrganizationStepName
                              }
                              editable={
                                item.process.processSteps.find(
                                  (e: ProcessSteps) =>
                                    e.stepName === 'WorkReportFilling'
                                )?.stepStatus === 1
                              }
                              signable={enableStudentWorkReportSignature(
                                item.processSteps
                              )}
                              alreadySigned={isSigned(
                                item.processSteps?.find(
                                  (e) =>
                                    e.stepName === 'SigningWorkReportByStudent'
                                )
                              )}
                              onSubmit={true}
                              onSubmitSuccess={() => {
                                queryClient.invalidateQueries({
                                  queryKey:
                                    QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
                                });
                              }}
                              workReport={file}
                              setWorkReport={setFile}
                              role={Roles.Student}
                              onFileUploadSuccess={async () => {
                                updateSteps(item.process?.id);
                              }}
                            />
                          </ConfigProvider>
                        </>
                      )}
                    <br />
                    {item.process &&
                      item.process.evaluationReportId !== 0 && (
                        <Divider plain style={{ fontSize: 20 }}>
                          {t('internshipProcessComp.evaluationReport')}
                        </Divider>
                      ) && (
                        <EvaluationReportDisplay
                          organizationId={item.process.organizationId}
                          reportId={item.process.evaluationReportId}
                          isSignable={false}
                          studentName={
                            loggedInUser.firstName + loggedInUser.lastName
                          }
                        />
                      )}
                  </>
                ),
              };
            }
          )}
        />
      </Space>
    );
  } else {
    return (
      <Space direction="vertical">
        <Text>{t('internshipProcessComp.noProcesses')}</Text>
      </Space>
    );
  }
};

export default InternshipProcessComponent;
